import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
} from '@stencil/core';
import {
    isBrowserIE,
    ThemeType,
} from '@uipath/portal-shell-util';

import { AccountType } from '../../constants/constants';
import { EcommerceEvent } from '../../models/appInsights/events/ecommerce.events';
import { AlertDismissedEvent } from '../../models/events/alertDismissedEvent';
import store from '../../store/store';
import { AccountAndTenantsInstanceResolver } from '../../utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { translatedContactSalesUrl } from '../../utils/helper';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import { tComponent } from '../../utils/localization/locComponent';
import Logger from '../../utils/logger/logger';
import { getTheme } from '../../utils/themeHelper';

/**
 * @slot - Content shown inside the alert bar
 */

@Component({
    tag: 'portal-alert-bar',
    styleUrl: 'portal-alert-bar.scss',
    shadow: true,
})
export class PortalAlertBar {

    @Element() elm: HTMLPortalAlertBarElement;

    /**
     * show only on IE
     * @memberof PortalAlertBar
     * @default false
     */
    @Prop() ieonly?: boolean;

    /**
     * The status of alert-bar
     * @memberof PortalAlertBar
     * @default info
     */
    @Prop() status: string = 'info';

    /**
     * The end date for trial alert-bar
     * @memberof PortalAlertBar
     * @default 0
     */
    @Prop() endDate: number = 0;

    /**
     * Show the contact sales button (generally for admins only)
     * @memberof PortalAlertBar
     * @default false
     */
    @Prop() showContactSalesButton: boolean = false;

    /**
     * The subscription type of alert-bar
     * @memberof PortalAlertBar
     */
    @Prop() subscriptionType: AccountType;

    /**
     * The cancelable alert-bar
     * @memberof PortalAlertBar
     * @default true
     */
    @Prop() cancelable: boolean = true;

    /**
     * The visibility of alert-bar
     * @memberof PortalAlertBar
     */
    @State() visible: boolean = true;

    /**
    * Theme override to bypass store state
    * @memberof PortalAlertBar
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Change the alignment of the alert bar to left aligned for trial status only
     * @memberof PortalAlertBar
     */
    @Prop() alignTrialLeft: boolean = false;

    /**
     * The service name for the trial alert bar
     * @memberof PortalAlertBar
     */
    @Prop() trialServiceName: string = '';

    /**
    * Trigger a custom event for alert being dismissed
    * @event PortalAlertBar
    */
    @Event({
        eventName: 'alertDismissed',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) alertDismiss: EventEmitter<AlertDismissedEvent>;

    connectedCallback() {
        if (this.ieonly && !isBrowserIE()) {
            this.visible = false;
        }
    }

    render() {
        return (<Host class={getTheme(store, this.theme)}>
            {this.visible &&
                <div class={'alert-bar-root ' + this.status} data-test="portal-alert-bar">
                    <div class="alert-bar-content">
                        <div class="alert-bar-left">
                            {this.status !== 'trial' &&
                                <div class="alert-bar-icon">
                                    <portal-custom-icon size="24px" name={this.status} theme={this.theme} />
                                </div>
                            }
                            {this.status === 'trial' && this.alignTrialLeft && this.getTrialContent()}
                        </div>
                        {!this.alignTrialLeft &&
                            <div class="pt-slot-content">
                                <slot></slot>
                            </div>
                        }
                        {this.status === 'trial' && !this.alignTrialLeft && this.getTrialContent()}
                        {this.cancelable &&
                            <div class="alert-bar-right">
                                <portal-icon-button
                                    type="icon"
                                    name="close"
                                    onClick={
                                        () => {
                                            this.alertDismiss.emit({ alertId: this.elm.id });
                                        }}
                                    aria-label={t('dismiss', getLanguage(store))}
                                    // Must be dark for trial because of the fixed background
                                    theme={this.status === 'trial' ? 'dark' : this.theme}
                                />
                            </div>
                        }
                    </div>
                </div>
            }
        </Host>
        );
    }

    private getTimeLeft(currentDate: number, endDate: number) {
        const hour = 60 * 60 * 1000;
        const differenceInHours = Math.round(
            (endDate - currentDate) / hour);
        if (differenceInHours > 24) {
            const differenceInDays = Math.round(differenceInHours / 24);
            const translationKey = `client-days-per-service${this.trialServiceName ? '-with-name' : ''}`;
            return tComponent(translationKey, getLanguage(store), {
                differenceInDays: <b>{ differenceInDays }</b>,
                serviceName: this.trialServiceName,
            });
        } else if (differenceInHours > 0) {
            const translationKey = `client-hours-per-service${this.trialServiceName ? '-with-name' : ''}`;
            return tComponent(translationKey, getLanguage(store), {
                differenceInHours: <b>{ differenceInHours }</b>,
                serviceName: this.trialServiceName,
            });
        }
        return '';
    }

    private onContactSalesClicked = () => {
        Logger.logCustomEvent(EcommerceEvent.ClickContactSales, {
            invocationMethod: 'PerServiceAlertTrialBarContactSales',
            subscriptionType: this.subscriptionType,
        });
    };

    private getTrialContent = () => {
        return (
            <div class={`trial-content${this.alignTrialLeft ? ' left-aligned' : ''}`}>
                <div class="trial-description">
                    {this.getTimeLeft(new Date().getTime(), new Date(this.endDate * 1000).getTime())}
                </div>
                {this.alignTrialLeft &&
                    <div class="pt-slot-content">
                        <slot></slot>
                    </div>
                }
                {
                    (this.showContactSalesButton || AccountAndTenantsInstanceResolver.Instance?.isAdminUserGroup()) &&
                    <portal-button
                        class="contact-sales-button"
                        cssClass="contact-sales-button"
                        label={t('Contact sales', getLanguage(store))}
                        type='primary'
                        size='sm'
                        href={translatedContactSalesUrl(getLanguage(store))}
                        onClick={this.onContactSalesClicked}
                    />
                }
            </div>
        );
    };
}
